import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import './styles/app.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { persistOptions, queryClient, resumeQueuedWrites } from './api/client';
import { setUnauthorizedHandler } from './lib/api';
import { initAppLock } from './lib/biometric';
import { signInPath } from './lib/entry';
import { initNative } from './lib/native';
import { initStorage } from './lib/storage';
import { toast } from './lib/toast';
import { initOnline } from './offline/online';
import { router } from './router';
import { session } from './session/session';

async function boot() {
  await initStorage();
  session.init();
  void initAppLock(); // locks synchronously before the first render
  initOnline();

  // Restore the cache (roster, today, queued writes) before the first route guard runs,
  // so the app opens straight into the Hub even with no signal.
  const [, restored] = persistQueryClient({ queryClient, ...persistOptions });
  try {
    await restored;
  } catch {
    /* corrupt cache: start empty */
  }
  void resumeQueuedWrites();

  // A 401 means the session was revoked or expired: keep queued writes (they replay after
  // the next sign-in) but send the user back to sign in.
  setUnauthorizedHandler(() => {
    if (!session.get().token) return;
    session.expire();
    toast('Your session ended — sign in again');
    void router.navigate({ to: signInPath() });
  });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );

  void initNative(router);
}

void boot();
